import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

/**
 * Convert an audio File/Blob to MP3 using FFmpeg WASM
 * Returns a File object with mime type 'audio/mpeg'
 * Supports all audio formats including WebM/Opus, OGG/Opus, etc.
 * If conversion fails, resolves with the original file.
 */
export async function convertToMp3(inputFileOrBlob) {
    let ffmpeg = null;

    try {
        // Create a File object from the input
        const inputFile = inputFileOrBlob instanceof File
            ? inputFileOrBlob
            : new File([inputFileOrBlob], `audio_${Date.now()}.webm`, { type: inputFileOrBlob.type || 'audio/webm' });

        // If it's already an MP3, return as is
        if (inputFile.type === 'audio/mpeg' || inputFile.name.toLowerCase().endsWith('.mp3')) {

            return inputFile;
        }



        // Initialize FFmpeg
        ffmpeg = new FFmpeg();

        // Load FFmpeg with proper CORS headers
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        await ffmpeg.load({
            coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
            wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });



        // Determine input file extension from mime type or filename
        const getInputExtension = (file) => {
            if (file.name && file.name.includes('.')) {
                return file.name.split('.').pop().toLowerCase();
            }

            // Map common MIME types to extensions
            const mimeToExt = {
                'audio/webm': 'webm',
                'audio/ogg': 'ogg',
                'audio/wav': 'wav',
                'audio/mp4': 'm4a',
                'audio/aac': 'aac',
                'audio/flac': 'flac',
                'audio/x-m4a': 'm4a'
            };

            return mimeToExt[file.type] || 'webm';
        };

        const inputExt = getInputExtension(inputFile);
        const inputFileName = `input.${inputExt}`;
        const outputFileName = 'output.mp3';

        // Write input file to FFmpeg virtual filesystem
        await ffmpeg.writeFile(inputFileName, await fetchFile(inputFile));



        // Convert to MP3 with good quality settings
        await ffmpeg.exec([
            '-i', inputFileName,           // Input file
            '-codec:a', 'libmp3lame',      // Use LAME MP3 encoder
            '-b:a', '128k',                // Audio bitrate 128kbps
            '-ar', '44100',                // Sample rate 44.1kHz
            '-ac', '2',                    // Stereo (2 channels)
            '-f', 'mp3',                   // Output format MP3
            outputFileName                 // Output file
        ]);



        // Read the converted file
        const mp3Data = await ffmpeg.readFile(outputFileName);

        // Create MP3 File object
        const originalName = inputFile.name || `audio_${Date.now()}`;
        const baseName = originalName.replace(/\.[^/.]+$/, "");
        const mp3File = new File([mp3Data], `${baseName}.mp3`, { type: 'audio/mpeg' });


        return mp3File;

    } catch (err) {
        console.error('FFmpeg conversion failed:', err);

        // Fallback: Try the old AudioContext method for supported formats
        try {

            return await convertWithAudioContext(inputFileOrBlob);
        } catch (fallbackErr) {
            console.warn('Both FFmpeg and AudioContext conversion failed, returning original file', fallbackErr);
            return inputFileOrBlob;
        }
    } finally {
        // Clean up FFmpeg instance
        if (ffmpeg) {
            try {
                ffmpeg.terminate();
            } catch (e) {
                console.warn('Error terminating FFmpeg:', e);
            }
        }
    }
}

/**
 * Fallback conversion using AudioContext + lamejs
 * Only works for formats that the browser can natively decode
 */
async function convertWithAudioContext(inputFileOrBlob) {
    const { Mp3Encoder } = await import('lamejs');

    const blob = inputFileOrBlob instanceof Blob ? inputFileOrBlob : new Blob([inputFileOrBlob]);
    const arrayBuffer = await blob.arrayBuffer();

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
        throw new Error("AudioContext not available in this browser");
    }

    const audioCtx = new AudioContext();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

    const numChannels = audioBuffer.numberOfChannels || 1;
    const sampleRate = audioBuffer.sampleRate || 44100;

    // Get mono samples (average channels if >1)
    const left = audioBuffer.getChannelData(0);
    let samples = left;
    if (numChannels > 1) {
        const right = audioBuffer.getChannelData(1);
        const len = Math.min(left.length, right.length);
        const merged = new Float32Array(len);
        for (let i = 0; i < len; i++) {
            merged[i] = (left[i] + right[i]) / 2;
        }
        samples = merged;
    }

    // Convert float samples [-1,1] to 16-bit PCM
    const maxSample = 32767;
    const sampleCount = samples.length;
    const mp3encoder = new Mp3Encoder(1, sampleRate, 128);
    const mp3Data = [];
    const sampleBlockSize = 1152;

    for (let i = 0; i < sampleCount; i += sampleBlockSize) {
        const slice = samples.subarray(i, i + sampleBlockSize);
        const buffer = new Int16Array(slice.length);
        for (let j = 0; j < slice.length; j++) {
            let s = Math.max(-1, Math.min(1, slice[j]));
            buffer[j] = s < 0 ? s * maxSample : s * maxSample;
        }
        const mp3buf = mp3encoder.encodeBuffer(buffer);
        if (mp3buf.length > 0) mp3Data.push(new Int8Array(mp3buf));
    }

    const mp3buf = mp3encoder.flush();
    if (mp3buf.length > 0) mp3Data.push(new Int8Array(mp3buf));

    const blobParts = mp3Data;
    const mp3Blob = new Blob(blobParts, { type: 'audio/mpeg' });

    if (audioCtx && typeof audioCtx.close === 'function') await audioCtx.close();

    // Construct a File preserving original base name if available
    const originalName = inputFileOrBlob.name || `audio_${Date.now()}`;
    const baseName = originalName.replace(/\.[^/.]+$/, "");
    const mp3File = new File([mp3Blob], `${baseName}.mp3`, { type: 'audio/mpeg' });

    return mp3File;
}

export default convertToMp3;
